import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";

/**
 * <RowActions> — one primary button + a "⋯" overflow menu for the rest.
 * Prevents the "6 tiny icons per row" anti-pattern.
 *
 * <RowActions
 *   primary={{ label: "Open", onClick: () => nav(url) }}
 *   items={[
 *     { label: "Send reminder", icon: Mail, onClick: fn },
 *     { label: "Delete", icon: Trash2, onClick: fn, danger: true },
 *   ]}
 * />
 */
export default function RowActions({ primary, items = [], className = "", testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className={`flex items-center justify-end gap-1 ${className}`} data-testid={testId} ref={ref}>
      {primary ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); primary.onClick(); }}
          data-testid={primary.testId}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-[#0A2540] text-white text-xs font-semibold hover:bg-[#12365b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A356] transition"
        >
          {primary.icon ? <primary.icon className="w-3.5 h-3.5" /> : null}
          {primary.label}
        </button>
      ) : null}

      {items.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={open}
            data-testid={testId ? `${testId}-more` : undefined}
            className="w-8 h-8 grid place-items-center rounded-md text-[#475569] dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A356] transition"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {open && (
            <div
              role="menu"
              className="absolute right-0 mt-1 min-w-[180px] rounded-md bg-white dark:bg-slate-900 border border-[#E2E8F0] dark:border-slate-700 shadow-lg z-20 py-1"
            >
              {items.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); setOpen(false); it.onClick(); }}
                  disabled={it.disabled}
                  data-testid={it.testId}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-xs transition ${
                    it.danger
                      ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      : "text-[#0A2540] dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {it.icon ? <it.icon className="w-3.5 h-3.5" /> : null}
                  {it.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
